import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  BookOpenText,
  Check,
  ChevronDown,
  ChevronLeft,
  ListTree,
  Printer,
  Search,
  ShieldCheck,
  X,
} from "lucide-react";
import {
  ambassadorGuideRoles,
  ambassadorGuideSections,
  AmbassadorGuideSection,
} from "../../data/ambassadorGuide";
import "./OperatorGuide.css";

const normalize = (value: string) =>
  value
    .normalize("NFKC")
    .toLowerCase()
    .replace(/ي/g, "ی")
    .replace(/ك/g, "ک")
    .replace(/[\u064b-\u065f\u0670]/g, "")
    .replace(/[\u200c\u200d\u200e\u200f]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

function SectionContent({ section }: { section: AmbassadorGuideSection }) {
  return (
    <>
      <p className="og-section-summary">{section.summary}</p>
      <ol className="og-steps">
        {section.steps.map((step, index) => (
          <li key={step}>
            <span aria-hidden="true">
              {(index + 1).toLocaleString("fa-IR")}
            </span>
            <p>{step}</p>
          </li>
        ))}
      </ol>
      {!!section.notes?.length && (
        <div className="og-notes">
          <strong>
            <ShieldCheck size={15} aria-hidden="true" /> نکته‌های مهم
          </strong>
          <ul>
            {section.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      )}
      {section.example && (
        <div className="og-example">
          <strong>{section.example.title}</strong>
          <p>{section.example.text}</p>
        </div>
      )}
    </>
  );
}

export default function OperatorGuide({
  open,
  onClose,
  roles,
}: {
  open: boolean;
  onClose: () => void;
  roles: string[];
}) {
  const id = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("ALL");
  const [contentsOpen, setContentsOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(
    () =>
      new Set(ambassadorGuideSections.slice(0, 1).map((section) => section.id)),
  );
  const ownRoles = ambassadorGuideRoles.filter((item) =>
    roles.includes(item.key),
  );

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (!open) {
      if (element.open) {
        element.close();
        opener.current?.focus({ preventScroll: true });
      }
      return;
    }
    if (!element.open) {
      opener.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      element.showModal();
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    searchInput.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const visibleSections = useMemo(() => {
    const terms = normalize(search).split(" ").filter(Boolean);
    return ambassadorGuideSections.filter((section) => {
      const matchesRole =
        role === "ALL" || !section.roles.length || section.roles.includes(role);
      const text = normalize(
        [
          section.title,
          section.summary,
          ...section.steps,
          ...(section.notes || []),
          section.example?.title || "",
          section.example?.text || "",
        ].join(" "),
      );
      return matchesRole && terms.every((term) => text.includes(term));
    });
  }, [role, search]);

  useEffect(() => {
    if (normalize(search)) {
      setExpanded(new Set(visibleSections.map((section) => section.id)));
    }
  }, [search, visibleSections]);

  const toggle = (sectionId: string) =>
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  const jumpTo = (sectionId: string) => {
    setExpanded((previous) => new Set([...Array.from(previous), sectionId]));
    setContentsOpen(false);
    window.requestAnimationFrame(() => {
      const heading = document.getElementById(`${id}-heading-${sectionId}`);
      heading?.focus({ preventScroll: true });
      heading?.scrollIntoView?.({
        block: "start",
        behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)")
          .matches
          ? "auto"
          : "smooth",
      });
    });
  };

  return (
    <dialog
      className="amb-operator-guide"
      ref={dialog}
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-description`}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div className="og-screen-content">
        <header className="og-header">
          <div className="og-heading-icon" aria-hidden="true">
            <BookOpenText size={25} />
          </div>
          <div className="og-heading-copy">
            <span>مرجع روزمره تیم سفیران</span>
            <h2 id={`${id}-title`}>راهنمای پنل سفیران</h2>
            <p id={`${id}-description`}>
              از بررسی اولین درخواست تا پیگیری همکاری؛ کارها را با ترتیب روشن
              انجام دهید.
            </p>
          </div>
          <button
            type="button"
            className="og-close"
            onClick={onClose}
            aria-label="بستن راهنما"
          >
            <X size={20} />
          </button>
        </header>

        <div className="og-body">
          <div className="og-intro">
            <div>
              <strong>اولین بار است با پنل کار می‌کنید؟</strong>
              <p>
                از «{ambassadorGuideSections[0]?.title}» شروع کنید؛ سپس راهنمای
                مربوط به کار خود را بخوانید. باز کردن و بستن راهنما، فرم فعلی
                شما را حفظ می‌کند.
              </p>
            </div>
            <button
              type="button"
              className="og-print"
              onClick={() => window.print()}
            >
              <Printer size={16} /> چاپ راهنما
            </button>
          </div>
          <div className="og-controls">
            <label className="og-search" htmlFor={`${id}-search`}>
              <Search size={18} aria-hidden="true" />
              <span className="og-sr-only">جست‌وجو در راهنما</span>
              <input
                id={`${id}-search`}
                ref={searchInput}
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="مثلاً تأیید درخواست، فعال‌سازی یا پشتیبانی…"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    searchInput.current?.focus();
                  }}
                  aria-label="پاک کردن جست‌وجو"
                >
                  <X size={15} />
                </button>
              )}
            </label>
            <label className="og-role-filter" htmlFor={`${id}-role`}>
              نمایش راهنما برای
              <select
                id={`${id}-role`}
                value={role}
                onChange={(event) => setRole(event.target.value)}
              >
                <option value="ALL">همه مسئولیت‌ها</option>
                {ambassadorGuideRoles.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="og-role-context">
            <ShieldCheck size={14} aria-hidden="true" />
            <p>
              مسئولیت فعلی شما:{" "}
              <strong>
                {ownRoles.map((item) => item.label).join("، ") || "تعیین نشده"}
              </strong>
              . فیلتر راهنما فقط مطالب را دسته‌بندی می‌کند؛ دکمه‌های پنل مطابق
              دسترسی حساب شما نمایش داده می‌شوند.
            </p>
          </div>

          <div className="og-layout">
            <aside
              className={`og-contents${contentsOpen ? " og-contents-open" : ""}`}
            >
              <button
                type="button"
                className="og-contents-toggle"
                aria-label="فهرست موضوعات راهنما"
                aria-expanded={contentsOpen}
                aria-controls={`${id}-contents`}
                onClick={() => setContentsOpen((previous) => !previous)}
              >
                <ListTree size={17} aria-hidden="true" /> مسیر راهنما
                <ChevronDown size={16} aria-hidden="true" />
              </button>
              <h3 className="og-contents-heading">
                <ListTree size={17} aria-hidden="true" /> مسیر راهنما
              </h3>
              <div id={`${id}-contents`} className="og-contents-inner">
                <nav aria-label="فهرست موضوعات راهنما">
                  {visibleSections.map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => jumpTo(section.id)}
                    >
                      <span>
                        {(
                          ambassadorGuideSections.indexOf(section) + 1
                        ).toLocaleString("fa-IR")}
                      </span>
                      <span>{section.title}</span>
                      <ChevronLeft size={13} aria-hidden="true" />
                    </button>
                  ))}
                </nav>
                <details className="og-role-directory">
                  <summary>هر مسئولیت چه کاری انجام می‌دهد؟</summary>
                  {ambassadorGuideRoles.map((item) => (
                    <div key={item.key}>
                      <strong>{item.label}</strong>
                      <p>{item.description}</p>
                    </div>
                  ))}
                </details>
              </div>
            </aside>
            <div className="og-articles">
              <div className="og-results-bar">
                <span role="status" aria-live="polite">
                  {visibleSections.length.toLocaleString("fa-IR")} موضوع از{" "}
                  {ambassadorGuideSections.length.toLocaleString("fa-IR")}
                </span>
                <div>
                  <button
                    type="button"
                    onClick={() =>
                      setExpanded(
                        new Set(visibleSections.map((section) => section.id)),
                      )
                    }
                  >
                    باز کردن همه بخش‌ها
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setExpanded(new Set());
                      setSearch("");
                    }}
                  >
                    جمع کردن بخش‌ها
                  </button>
                </div>
              </div>
              {visibleSections.length ? (
                visibleSections.map((section) => {
                  const isExpanded = expanded.has(section.id);
                  return (
                    <article
                      key={section.id}
                      className={`og-section${isExpanded ? " og-section-open" : ""}`}
                    >
                      <h3>
                        <button
                          type="button"
                          id={`${id}-heading-${section.id}`}
                          aria-expanded={isExpanded}
                          aria-controls={`${id}-section-${section.id}`}
                          onClick={() => toggle(section.id)}
                        >
                          <span
                            className="og-section-number"
                            aria-hidden="true"
                          >
                            {(
                              ambassadorGuideSections.indexOf(section) + 1
                            ).toLocaleString("fa-IR")}
                          </span>
                          <span>{section.title}</span>
                          <ChevronDown size={17} aria-hidden="true" />
                        </button>
                      </h3>
                      <div
                        id={`${id}-section-${section.id}`}
                        className="og-section-body"
                        role="region"
                        aria-labelledby={`${id}-heading-${section.id}`}
                        hidden={!isExpanded}
                      >
                        <SectionContent section={section} />
                      </div>
                    </article>
                  );
                })
              ) : (
                <div className="og-empty">
                  <Search size={30} aria-hidden="true" />
                  <h3>موضوعی با این جست‌وجو پیدا نشد</h3>
                  <p>
                    واژه کوتاه‌تری بنویسید یا نمایش همه مسئولیت‌ها را انتخاب
                    کنید.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSearch("");
                      setRole("ALL");
                      searchInput.current?.focus();
                    }}
                  >
                    نمایش همه موضوعات
                  </button>
                </div>
              )}
            </div>
          </div>
          <footer className="og-footer">
            <Check size={16} aria-hidden="true" />
            <p>
              پیش از ثبت هر تغییر، حساب سفیر و نتیجه موردنظر را بررسی کنید. اگر
              دکمه‌ای را نمی‌بینید، راهنمای دسترسی‌ها را بخوانید یا با سرپرست
              هماهنگ کنید.
            </p>
            <button type="button" onClick={onClose}>
              بازگشت به پنل
            </button>
          </footer>
        </div>
      </div>
      <div className="og-print-content" aria-hidden="true">
        <h1>راهنمای پنل سفیران من حقیقی</h1>
        <p>راهنمای کامل اپراتور؛ شامل همه مسئولیت‌های تیم</p>
        {ambassadorGuideSections.map((section, index) => (
          <section key={section.id}>
            <h2>
              {(index + 1).toLocaleString("fa-IR")}. {section.title}
            </h2>
            <SectionContent section={section} />
          </section>
        ))}
        <section>
          <h2>مسئولیت‌ها و دسترسی‌ها</h2>
          {ambassadorGuideRoles.map((item) => (
            <p key={item.key}>
              <strong>{item.label}: </strong>
              {item.description}
            </p>
          ))}
        </section>
      </div>
    </dialog>
  );
}
